import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect, useShallowCompareEffect } from 'react-use';

import { LANGUAGES } from '@savgroup-front-common/constants';
import { changeLocaleLanguage } from '@savgroup-front-common/core/domains/i18n/actions';
import { selectLocale } from '@savgroup-front-common/core/domains/i18n/selectors';
import { getAllAvailableLanguages } from '@savgroup-front-common/core/domains/languages/selectors';
import { useCurrentUser } from '@savgroup-front-common/core/domains/login/useLoginStore';
import { getSellersConfiguration } from '@savgroup-front-common/core/domains/sellerConfiguration/actionCreators';
import { sellerSelectors } from '@savgroup-front-common/core/domains/sellers';
import { sortLanguageByPrefered } from '@savgroup-front-common/core/helpers';
import { useChangeHTMLLanguage } from '@savgroup-front-common/core/hooks';
import {
  RUM_ACTION_NAME,
  trackRumAction,
} from '@savgroup-front-common/core/services';

import { useControlTypedSelector } from '../../../../../hooks';

const useDefaultLanguage = (): void => {
  const dispatch = useDispatch();
  const languages = useSelector(getAllAvailableLanguages);
  const sellerIds = useSelector(sellerSelectors.selectSellerIds);
  const { preferences, userId } = useCurrentUser();
  const locale = useControlTypedSelector(selectLocale);

  const currentLanguage = useSelector(selectLocale);

  useChangeHTMLLanguage(currentLanguage);

  useShallowCompareEffect(() => {
    if (sellerIds.length > 0) {
      dispatch(getSellersConfiguration({ sellerIds }));
    }
  }, [dispatch, sellerIds]);

  useDeepCompareEffect(
    function selectDefaultLanguage() {
      if (!languages) {
        return;
      }

      const availableLanguages: string[] = languages;

      const [nextCurrentLanguage] = sortLanguageByPrefered(availableLanguages);

      const userLanguagePreference = preferences.language?.toLowerCase();

      const onlyLangueAvailableIsntTheCurrentOne =
        availableLanguages.length === 1 &&
        currentLanguage !== nextCurrentLanguage;
      const isLanguageAvailable = availableLanguages.includes(currentLanguage);
      const isLoginLanguageAvailable =
        userLanguagePreference !== undefined &&
        availableLanguages.includes(userLanguagePreference);
      const isLocaleAvailable = availableLanguages.includes(locale);

      const shouldUpdate =
        onlyLangueAvailableIsntTheCurrentOne || !isLanguageAvailable;

      if (!isLoginLanguageAvailable) {
        trackRumAction(RUM_ACTION_NAME.LOGIN_LANGUAGE_UNAVAILABLE_FALLBACK, {
          userLanguagePreference,
          locale,
          currentLanguage,
          availableLanguages,
          fallbackLocale: isLocaleAvailable ? locale : LANGUAGES.EN,
          userId: userId,
          sellerIds,
        });

        dispatch(
          changeLocaleLanguage({
            locale: (isLocaleAvailable ? locale : LANGUAGES.EN) as LANGUAGES,
          }),
        );
      }

      if (
        nextCurrentLanguage !== undefined &&
        shouldUpdate &&
        isLoginLanguageAvailable
      ) {
        dispatch(
          changeLocaleLanguage({ locale: nextCurrentLanguage as LANGUAGES }),
        );
      }
    },
    [
      languages,
      currentLanguage,
      dispatch,
      preferences.language,
      userId,
      locale,
      sellerIds,
    ],
  );
};

export default useDefaultLanguage;
